// src/sections/Hero.js
import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebaseConfig/firebase";
import { personalInfo } from "../data";

const Hero = () => {
  const [heroData, setHeroData] = useState({
    name: personalInfo.name,
    title: personalInfo.title,
    backgroundType: "images",
    backgroundImages: [],
    videoUrl: "",
  });
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState([]);

  // Cargar datos desde Firestore
  useEffect(() => {
    const loadHeroData = async () => {
      try {
        const docRef = doc(db, "content", "hero");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setHeroData(data);
          // Inicializar estado de carga de imágenes
          if (data.backgroundType === "images" && data.backgroundImages) {
            setImagesLoaded(new Array(data.backgroundImages.length).fill(false));
          }
        }
      } catch (error) {
        console.error("Error al cargar datos de Hero:", error);
      } finally {
        setLoading(false);
      }
    };

    loadHeroData();
  }, []);

  // Precargar imágenes
  useEffect(() => {
    if (heroData.backgroundType === "images" && heroData.backgroundImages.length > 0) {
      const preloadImages = async () => {
        // Cargar la primera imagen inmediatamente
        const firstImg = new Image();
        firstImg.onload = () => {
          setImagesLoaded((prev) => {
            const newState = [...prev];
            newState[0] = true;
            return newState;
          });
        };
        firstImg.src = heroData.backgroundImages[0];

        // Cargar las demás imágenes progresivamente
        heroData.backgroundImages.slice(1).forEach((src, index) => {
          setTimeout(
            () => {
              const img = new Image();
              img.onload = () => {
                setImagesLoaded((prev) => {
                  const newState = [...prev];
                  newState[index + 1] = true;
                  return newState;
                });
              };
              img.src = src;
            },
            (index + 1) * 500,
          );
        });
      };

      preloadImages();
    }
  }, [heroData.backgroundImages, heroData.backgroundType]);

  // Auto-cambiar imágenes cada 10 segundos
  useEffect(() => {
    if (heroData.backgroundType === "images" && heroData.backgroundImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentImageIndex(
          (prevIndex) => (prevIndex + 1) % heroData.backgroundImages.length,
        );
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [heroData.backgroundImages.length, heroData.backgroundType]);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
    });
  };

  // Extraer ID del video de YouTube
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[7].length === 11 ? match[7] : null;
  };

  const videoId = getYouTubeVideoId(heroData.videoUrl);

  if (loading) {
    return (
      <section
        id="home"
        className="min-h-screen flex items-center justify-center bg-black"
      >
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </section>
    );
  }

  return (
    <section
      id="home"
      className="min-h-screen flex items-center justify-center pt-20 relative overflow-hidden"
    >
      {/* Background - Video o Imágenes */}
      <div className="absolute inset-0 z-0">
        {heroData.backgroundType === "video" && videoId ? (
          // Video de YouTube como fondo
          <div className="absolute inset-0">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
              title="Background video"
              allow="autoplay; encrypted-media"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[100vw] h-[100vh] pointer-events-none"
              style={{
                minWidth: "100vw",
                minHeight: "100vh",
                width: "177.77vh", // 16:9 aspect ratio
                height: "56.25vw", // 16:9 aspect ratio
              }}
            />
            {/* Overlay para mejorar legibilidad del texto */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          </div>
        ) : (
          // Carrusel de imágenes
          <>
            {heroData.backgroundImages.map((image, index) => (
              <div
                key={index}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                  index === currentImageIndex && imagesLoaded[index]
                    ? "opacity-100"
                    : "opacity-0"
                }`}
                style={{
                  backgroundImage: imagesLoaded[index] ? `url(${image})` : "none",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }}
              >
                {/* Overlay para mejorar legibilidad del texto */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
              </div>
            ))}

            {/* Fallback gradient si las imágenes no cargan */}
            {!imagesLoaded[currentImageIndex] && (
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-black"></div>
            )}
          </>
        )}
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        {/* Main Content - Ultra Minimal */}
        <div className="mb-20">
          {/* Name - Large and Simple */}
          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-light text-white mb-4 animate-fade-in tracking-wider">
            {heroData.name}
          </h1>

          {/* Title - Subtle */}
          <p className="text-lg sm:text-xl text-white/70 font-light tracking-widest uppercase animate-fade-in">
            {heroData.title}
          </p>
        </div>

        {/* Scroll Indicator - Minimal */}
        <div
          className="animate-bounce cursor-pointer animate-fade-in absolute bottom-8 left-1/2 transform -translate-x-1/2"
          style={{ animationDelay: "0.6s" }}
        >
          <button
            onClick={() => scrollToSection("projects")}
            className="text-white/50 hover:text-white transition-colors duration-300"
            aria-label="Scroll to projects"
          >
            <ChevronDown size={32} strokeWidth={1} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Hero;
